/**
 * DeCA API client for the web portal.
 *
 * Generated from `reference/deca-api/deca-openapi.yaml` — the frozen contract. Do not edit
 * `schema.d.ts` by hand: change the YAML and run `npm run generate`.
 */

export type { paths, components, operations } from './schema'

export {
  createDecaClient,
  unwrap,
  unwrapWithETag,
  ifMatch,
  fetchDecaPdf,
  type DecaClient,
  type DecaClientOptions,
} from './client.ts'

export {
  DecaApiError,
  isProblem,
  isValidationProblem,
  toApiError,
  blockingErrors,
  warnings,
  blocksGeneration,
  errorsForParty,
  partiesAtFault,
  errorsByField,
  penaltyNotice,
  type Problem,
  type ValidationProblem,
  type PartyErrors,
  type FieldError,
  type LiableParty,
} from './errors.ts'

import type { components } from './schema'

/**
 * Aliases for the schemas the portal touches constantly, so screens can say `Deca`
 * instead of `components['schemas']['Deca']`.
 */
export type Deca = components['schemas']['Deca']
export type DecaSummary = components['schemas']['DecaSummary']
export type DecaInput = components['schemas']['DecaInput']
export type DecaPatch = components['schemas']['DecaPatch']
export type DecaCommonFields = components['schemas']['DecaCommonFields']
export type DecaModifiableFields = components['schemas']['DecaModifiableFields']
export type DecaEstado = components['schemas']['DecaEstado']
export type VoidReason = components['schemas']['VoidReason']

/**
 * The passenger route sheet (Addendum No. 2) — the goods block's twin.
 *
 * Aliased rather than reached through as `components['schemas'][…]` for the reason the
 * goods block above is: three screens (create, the validation rail, detail) need these
 * same types, and a single import surface beats the same string literal retyped in six
 * files. `ServiceClassValue` in `web/src/deca/passenger/fields.ts` reaches through
 * instead, deliberately — it is one type read in one file.
 *
 * This file is hand-written; `schema.d.ts` is what `npm run generate` produces and what
 * `npm run check:drift` diffs. Adding an alias here is therefore not contract drift.
 */
export type PassengerDeca = components['schemas']['PassengerDeca']
export type PassengerDecaSummary = components['schemas']['PassengerDecaSummary']
export type PassengerDecaInput = components['schemas']['PassengerDecaInput']
export type PassengerDecaPatch = components['schemas']['PassengerDecaPatch']
export type PassengerDecaModifiableFields = components['schemas']['PassengerDecaModifiableFields']
export type PassengerService = components['schemas']['PassengerService']
export type PassengerServiceInput = components['schemas']['PassengerServiceInput']
/**
 * The route sheet's 422. Flat — `fieldErrors`, no `partyErrors` and no `penaltyNotice`,
 * because FOM/1230/2013 has no liable-party split. `isValidationProblem` in `errors.ts`
 * tests for `partyErrors` and so answers **false** for one of these; the portal's
 * `passengerValidationProblemOf` is the passenger-shaped test.
 */
export type PassengerValidationProblem = components['schemas']['PassengerValidationProblem']
export type PassengerFieldError = components['schemas']['PassengerFieldError']
export type PassengerHistoryEntry = components['schemas']['PassengerHistoryEntry']
export type PassengerVersionSummary = components['schemas']['PassengerVersionSummary']

export type Consignment = components['schemas']['Consignment']
export type ConsignmentInput = components['schemas']['ConsignmentInput']
export type UnidadMedida = components['schemas']['UnidadMedida']

export type DecaVersion = components['schemas']['DecaVersion']
export type DecaVersionSummary = components['schemas']['DecaVersionSummary']
export type HistoryEntry = components['schemas']['HistoryEntry']
export type AuditEvent = components['schemas']['AuditEvent']
export type ActorRef = components['schemas']['ActorRef']
/** The Gestor Individual who manages a document (Addendum No. 3, Point 7) — `owner` on the four document shapes. */
export type DocumentOwner = components['schemas']['DocumentOwner']
/**
 * How far a goods document is delivered — `delivery` on `Deca` and `DecaSummary`, `null` before the
 * first confirmation — and one confirmation as `listDeliveryConfirmations` lists it (#160).
 */
export type DeliverySummary = components['schemas']['DeliverySummary']
export type DeliveryConfirmation = components['schemas']['DeliveryConfirmation']

export type Organisation = components['schemas']['Organisation']
export type OrganisationSettings = components['schemas']['OrganisationSettings']
export type User = components['schemas']['User']
/** What became of a user's latest letter (#114) — `User.lastEmail`. */
export type EmailDelivery = components['schemas']['EmailDelivery']
/** A DeCA sent by email, and what became of each recipient's letter (#219). */
export type DecaEmailSending = components['schemas']['DecaEmailSending']
export type DecaEmailRecipient = components['schemas']['DecaEmailRecipient']
export type EmailAddressCheck = components['schemas']['EmailAddressCheck']
export type Role = components['schemas']['Role']
export type Scope = components['schemas']['Scope']
export type ApiKey = components['schemas']['ApiKey']
/** One of ASGTRANS's service accounts (#161), as its management lists it. */
export type ServiceAccount = components['schemas']['ServiceAccount']
/** A phone linked to the company, and a one-time code to link one (#110). */
export type PhoneLink = components['schemas']['PhoneLink']
export type PhoneLinkCode = components['schemas']['PhoneLinkCode']
export type PhoneLinkCodeCreated = components['schemas']['PhoneLinkCodeCreated']
export type AuthSession = components['schemas']['AuthSession']
export type CurrentPrincipal = components['schemas']['CurrentPrincipal']

export type ImportJob = components['schemas']['ImportJob']
export type ImportRow = components['schemas']['ImportRow']
export type ValidationResult = components['schemas']['ValidationResult']
export type RoadsideDeca = components['schemas']['RoadsideDeca']
export type PageMeta = components['schemas']['PageMeta']

/**
 * The document states, in lifecycle order. Exported as a value (not just a type) so the
 * list filter and the KPI row can iterate without re-declaring them and drifting from
 * the contract. Design spec §4 fixes the labels and the status-chip tokens.
 */
export const DECA_ESTADOS = [
  'BORRADOR',
  'EN CURSO',
  'MODIFICADO',
  'FINALIZADO',
  'CADUCADO',
  'ANULADO',
] as const satisfies readonly DecaEstado[]

/**
 * Units from the import template's `LISTAS` sheet, and `box` and `carros` since 2026-10-27 — the only
 * values the API accepts, in the order the portal offers them.
 */
export const UNIDADES_MEDIDA = [
  'kg',
  't',
  'l',
  'm3',
  'palets',
  'bultos',
  'unidades',
  'box',
  'carros',
] as const satisfies readonly UnidadMedida[]

/** States in which a generated PDF is downloadable without authentication. */
export const ACTIVE_ESTADOS = ['EN CURSO', 'MODIFICADO'] as const satisfies readonly DecaEstado[]
